import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

export type DemoLang = 'es' | 'en';

type Dictionary = Record<string, unknown>;

const STORAGE_KEY = 'demo-lang';

/**
 * Singleton hand-rolled i18n service (Transloco was not installable in this
 * environment — npm registry access is blocked). Exposes the same shape a
 * real i18n library would (a reactive `lang` signal + a `t(key)` lookup),
 * so it can be swapped later with minimal churn.
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {
  readonly lang = signal<DemoLang>(this.readInitialLang());
  private dictionaries: Partial<Record<DemoLang, Dictionary>> = {};
  private loading: Partial<Record<DemoLang, Promise<void>>> = {};

  constructor(private readonly http: HttpClient) {
    void this.ensureLoaded(this.lang());
  }

  async setLang(lang: DemoLang): Promise<void> {
    await this.ensureLoaded(lang);
    this.lang.set(lang);
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // ignore
    }
  }

  toggle(): void {
    void this.setLang(this.lang() === 'es' ? 'en' : 'es');
  }

  /** Dot-path lookup, e.g. t('nav.services'). Falls back to the key itself. */
  t(key: string): string {
    const dict = this.dictionaries[this.lang()];
    if (!dict) {
      return key;
    }
    const value = key.split('.').reduce<unknown>((acc, part) => {
      if (acc && typeof acc === 'object' && part in (acc as Dictionary)) {
        return (acc as Dictionary)[part];
      }
      return undefined;
    }, dict);
    return typeof value === 'string' ? value : key;
  }

  private async ensureLoaded(lang: DemoLang): Promise<void> {
    if (this.dictionaries[lang]) {
      return;
    }
    if (!this.loading[lang]) {
      this.loading[lang] = firstValueFrom(this.http.get<Dictionary>(`assets/i18n/${lang}.json`))
        .then((dict) => {
          this.dictionaries[lang] = dict;
        })
        .catch(() => {
          this.dictionaries[lang] = {};
        });
    }
    await this.loading[lang];
  }

  private readInitialLang(): DemoLang {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'es' || stored === 'en') {
        return stored;
      }
    } catch {
      // ignore
    }
    const nav = typeof navigator !== 'undefined' ? navigator.language : 'es';
    return nav.toLowerCase().startsWith('en') ? 'en' : 'es';
  }
}
